import React, { useState } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import Title from '@/components/Title';
import Link, { LinkStyle, LinkType } from './Link';
import Badge, { BadgeType } from './Badge';
import About, { AboutType } from './About';
import { config } from '@keytrade/functions';
import Image, { ImageType } from '@/components/Image';
import VideoLink, { VideoLinkType } from './VideoLink';
import { Icon } from '@keytrade/components-icon';
import { mediaQuery } from '@/utils/helpers';

import { COOKIE_CATEGORIES } from '@/utils/constants';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { videoPlatforms } from '@/utils/video/video.utils';
import useVideo from '@/hooks/useVideo';

const { list: colors } = config.colors;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  text-align: center;
`;
const ImageContent = styled(Image)`
  border-radius: 24px;
  isolation: isolate;
`;

//make the image go full-width even if the image is too small
const Card = styled.li`
  display: flex;
  > a {
    display: block;
    width: 100%;
    img {
      max-width: none;
      width: 100%;
    }
  }
`;
const ImageAndBadgeWrapper = styled.div`
  position: relative;
  display: flex;
  overflow: visible;
  margin-bottom: 2.4rem;
  width: 100%;
`;
const BadgeWrapper = styled.div`
  position: absolute;
  z-index: 10;
  bottom: -7px;
  left: 50%;
  transform: translateX(-50%);
`;

const PlayButton = styled.a`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 48px;
  width: 48px;
  background-color rgba(0,0,0, 0.7);
  height: 48px;
  width: 48px;
  border-radius: 50%;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
`;
const BackgroundLayer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(60, 84, 99, 0.75);
  pointer-events: all;
  transition: opacity 0.2s cubic-bezier(0.23, 1, 0.32, 1);
`;

const VideoModal = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 1080px;
  width: 100%;
  height: 300px;
  padding: 0 20px;
  ${mediaQuery.md(`
    height: 450px;
  `)}
  ${mediaQuery.xl(`
    height: 608px;
    padding: 0;
  `)}
`;

const CloseWrapper = styled.a`
  display: inline-flex;
  justify-content: center;
  position: absolute;
  right: 20px;
  top: -40px;
  width: 32px;
  height: 32px;
  align-items: center;
  background-color: ${colors.BlueDark};
  border-radius: 50%;
  ${mediaQuery.xl(`
    right: -44px;
    top: 24px;
  `)}
`;

const CardContentWrapper = styled.div`
  display: block;
  width: 100%;
`;

type imageProps = {
  link?: string;
};

//the calculation is based of what the images should be
//the ratio is 1:1 (padding-top) as it should size and remain a square
//check graphql (image) we force 336px on 336px which is a 1:1 ratio
const BgImage = styled.div<imageProps>`
  background: url(${({ link }) => link});
  background-position: center;
  background-size: cover;
  padding-top: calc(336 / 336 * 100%);
  width: 100%;
  border-radius: 24px;
  isolation: isolate;
`;

export type Props = SmallCardData;

export type SmallCardData = {
  badge: BadgeType;
  about: AboutType;
  title: string;
  dark: boolean;
  link?: LinkType;
  image?: ImageType;
  videoLink?: VideoLinkType;
};

const SmallCard: React.FC<Props> = ({
  link,
  title,
  badge,
  image,
  about,
  dark,
  videoLink,
}) => {
  const [isVideoId, setVideoId] = useState('');

  const { videoUrl, videoThumbnail, videoPlatform } = useVideo(videoLink?.url);

  const closeVideoModal = () => {
    setVideoId('');
  };

  return (
    <>
      {videoLink ? (
        <>
          <Card>
            <CardContent>
              {videoLink ? (
                <ImageAndBadgeWrapper>
                  <BgImage link={videoThumbnail} />
                  <PlayButton onClick={() => setVideoId(videoUrl)}>
                    <Icon icon='icn_playLight' color='#ffffff' size={'3rem'} />
                  </PlayButton>
                </ImageAndBadgeWrapper>
              ) : (
                <ImageAndBadgeWrapper>
                  <ImageContent {...image} />
                  {badge && (
                    <BadgeWrapper>
                      <Badge {...badge} />
                    </BadgeWrapper>
                  )}
                </ImageAndBadgeWrapper>
              )}
              {about && <About data={about} dark={dark} small />}

              <Title size='sm' level='h3' lightColor={dark}>
                {title}
              </Title>
            </CardContent>
          </Card>
          {isVideoId && (
            <Overlay>
              <BackgroundLayer onClick={closeVideoModal}>
                <VideoModal>
                  <VideoLink
                    backgroundColor={colors.White}
                    videoHeight='608px'
                    url={videoLink.url}
                  ></VideoLink>
                  <CloseWrapper onClick={closeVideoModal}>
                    <Icon
                      icon='icn_cross'
                      size='20px'
                      color={`${colors.White}`}
                    />
                  </CloseWrapper>
                </VideoModal>
              </BackgroundLayer>
            </Overlay>
          )}
        </>
      ) : (
        <Card>
          {link ? (
            <Link linkStyle={LinkStyle.CONTENT} {...link}>
              <CardContent>
                {image && (
                  <ImageAndBadgeWrapper>
                    <ImageContent {...image} />

                    {badge && (
                      <BadgeWrapper>
                        <Badge {...badge} />
                      </BadgeWrapper>
                    )}
                  </ImageAndBadgeWrapper>
                )}
                {about && <About data={about} dark={dark} small />}

                <Title size='sm' level='h3' lightColor={dark}>
                  {title}
                </Title>
              </CardContent>
            </Link>
          ) : (
            <Card>
              <CardContentWrapper>
                <CardContent>
                  {image && (
                    <ImageAndBadgeWrapper>
                      <ImageContent {...image} />

                      {badge && (
                        <BadgeWrapper>
                          <Badge {...badge} />
                        </BadgeWrapper>
                      )}
                    </ImageAndBadgeWrapper>
                  )}
                  {about && <About data={about} dark={dark} small />}

                  <Title size='sm' level='h3' lightColor={dark}>
                    {title}
                  </Title>
                </CardContent>
              </CardContentWrapper>
            </Card>
          )}
        </Card>
      )}
    </>
  );
};
export default SmallCard;

export const smallCardFragmentQuery = graphql`
  fragment SmallCardFragment on ContentfulSmallCard {
    __typename
    badge {
      ...BadgeFragment
    }
    about {
      ...AboutFragment
    }
    image {
      gatsbyImageData(width: 336, height: 336)
      ...ImageFragment
    }
    videoLink {
      url
    }
    title
    link {
      ...LinkFragment
    }
  }
`;
